import { clearAccountSecuritySetupIntent } from '@/lib/auth/accountSecuritySetupIntent';
import { SIGNED_OUT_PATH } from '@/lib/auth/appAccess';
import { type BrowserLogoutOptions, getBrowserLogoutUrl } from '@/lib/auth/browserLogout';
import { clearSessionActiveOrgId } from '@/lib/utils/activeOrganisation';
import { signOut } from 'next-auth/react';

type HandleSignOutOptions = {
  callbackUrl?: string;
  skipConfirmation?: boolean;
};

let signOutInFlight: Promise<void> | null = null;

const getCallbackUrl = (value: HandleSignOutOptions | unknown): string => {
  if (value != null && typeof value === 'object' && 'callbackUrl' in value) {
    const callbackUrl = value.callbackUrl;

    if (typeof callbackUrl === 'string' && callbackUrl.length > 0) {
      if (typeof window !== 'undefined') {
        return new URL(callbackUrl, window.location.origin).toString();
      }

      return callbackUrl;
    }
  }

  if (typeof window !== 'undefined') {
    return new URL(SIGNED_OUT_PATH, window.location.origin).toString();
  }

  return SIGNED_OUT_PATH;
};

type SignOutFlowDependencies = {
  clearActiveOrganisation: () => void;
  clearAccountSecuritySetup: () => void;
  getLogoutUrl: (callbackUrl: string, options?: BrowserLogoutOptions) => Promise<string | null>;
  redirect: (url: string) => void;
  signOut: (options: { callbackUrl: string; redirect: false }) => Promise<unknown>;
};

type SignOutFlowOptions = {
  skipConfirmation?: boolean;
};

export async function runSignOutFlow(
  callbackUrl: string,
  {
    clearAccountSecuritySetup,
    clearActiveOrganisation,
    getLogoutUrl,
    redirect,
    signOut: signOutSession,
  }: SignOutFlowDependencies,
  options: SignOutFlowOptions = {}
): Promise<void> {
  const fallbackCallbackUrl = callbackUrl;
  let browserLogoutUrl: string | null = null;

  try {
    const skipConfirmation = options.skipConfirmation === true;

    browserLogoutUrl = skipConfirmation
      ? await getLogoutUrl(callbackUrl, { skipConfirmation: true })
      : await getLogoutUrl(callbackUrl);

    if (!skipConfirmation && browserLogoutUrl != null) {
      redirect(browserLogoutUrl);

      return;
    }

    clearActiveOrganisation();
    clearAccountSecuritySetup();

    await signOutSession({ callbackUrl, redirect: false });
    redirect(browserLogoutUrl ?? fallbackCallbackUrl);
  } catch (error) {
    console.error('Sign out failed:', error);
    redirect(browserLogoutUrl ?? fallbackCallbackUrl);
  }
}

export const handleSignOut = async (options?: HandleSignOutOptions) => {
  const callbackUrl = getCallbackUrl(options);

  if (signOutInFlight != null) {
    return signOutInFlight;
  }

  signOutInFlight = (async () => {
    await runSignOutFlow(
      callbackUrl,
      {
        clearActiveOrganisation: clearSessionActiveOrgId,
        clearAccountSecuritySetup: clearAccountSecuritySetupIntent,
        getLogoutUrl: getBrowserLogoutUrl,
        redirect: (url) => {
          if (typeof window !== 'undefined') {
            window.location.replace(url);
          }
        },
        signOut,
      },
      { skipConfirmation: options?.skipConfirmation === true }
    );

    signOutInFlight = null;
  })();

  try {
    await signOutInFlight;
  } finally {
    signOutInFlight = null;
  }
};
